import React, { useState, useEffect } from 'react';
import {
  Mail,
  Plus,
  Edit2,
  Trash2,
  Send,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  AlertCircle,
  BarChart3,
  FileText,
  Zap,
  Calendar,
  RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { adminAPI } from '@/utils/api';
import { toast } from 'sonner';

// Trigger types avec labels français
const TRIGGER_TYPES = {
  form_submitted: 'Formulaire soumis',
  account_created: 'Compte cree',
  payment_completed: 'Paiement effectue'
};

// Delay presets
const DELAY_PRESETS = [
  { value: 0, label: 'Immediat' },
  { value: 60, label: '1 heure' },
  { value: 1440, label: 'J+1 (24h)' },
  { value: 4320, label: 'J+3 (72h)' },
  { value: 7200, label: 'J+5 (120h)' },
  { value: 10080, label: 'J+7 (168h)' }
];

const AdminEmails = () => {
  const [activeTab, setActiveTab] = useState('stats');
  const [templates, setTemplates] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [scheduledEmails, setScheduledEmails] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form states
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [testEmail, setTestEmail] = useState('');

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'templates' || activeTab === 'campaigns') {
        const templatesRes = await adminAPI.getEmailTemplates();
        setTemplates(templatesRes.data);
      }
      if (activeTab === 'campaigns') {
        const campaignsRes = await adminAPI.getEmailCampaigns();
        setCampaigns(campaignsRes.data);
      }
      if (activeTab === 'logs') {
        const logsRes = await adminAPI.getEmailLogs({ limit: 50 });
        setLogs(logsRes.data.logs || []);
      }
      if (activeTab === 'stats') {
        const [statsRes, scheduledRes] = await Promise.all([
          adminAPI.getEmailStats(),
          adminAPI.getScheduledEmails({ status: 'pending', limit: 10 })
        ]);
        setStats(statsRes.data);
        setScheduledEmails(scheduledRes.data.scheduled || []);
      }
    } catch (error) {
      console.error('Error fetching email data:', error);
      toast.error('Erreur lors du chargement des donnees');
    } finally {
      setLoading(false);
    }
  };

  // Template CRUD
  const handleSaveTemplate = async () => {
    if (!editingTemplate?.name || !editingTemplate?.subject || !editingTemplate?.html_content) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    try {
      if (editingTemplate.id) {
        await adminAPI.updateEmailTemplate(editingTemplate.id, {
          name: editingTemplate.name,
          subject: editingTemplate.subject,
          html_content: editingTemplate.html_content,
          is_active: editingTemplate.is_active
        });
        toast.success('Template mis a jour');
      } else {
        await adminAPI.createEmailTemplate({
          name: editingTemplate.name,
          subject: editingTemplate.subject,
          html_content: editingTemplate.html_content
        });
        toast.success('Template cree');
      }
      setEditingTemplate(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Supprimer ce template ?')) return;
    try {
      await adminAPI.deleteEmailTemplate(templateId);
      toast.success('Template supprime');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleTestEmail = async (templateId) => {
    if (!testEmail) {
      toast.error('Veuillez entrer un email');
      return;
    }
    try {
      await adminAPI.sendTestEmail(templateId, testEmail);
      toast.success('Email de test envoye');
      setTestEmail('');
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    }
  };

  // Campaign CRUD
  const handleSaveCampaign = async () => {
    if (!editingCampaign?.name || !editingCampaign?.trigger_type || !editingCampaign?.template_id) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    try {
      if (editingCampaign.id) {
        await adminAPI.updateEmailCampaign(editingCampaign.id, {
          name: editingCampaign.name,
          trigger_type: editingCampaign.trigger_type,
          delay_minutes: editingCampaign.delay_minutes,
          template_id: editingCampaign.template_id,
          is_active: editingCampaign.is_active
        });
        toast.success('Campagne mise a jour');
      } else {
        await adminAPI.createEmailCampaign({
          name: editingCampaign.name,
          trigger_type: editingCampaign.trigger_type,
          delay_minutes: editingCampaign.delay_minutes || 0,
          template_id: editingCampaign.template_id
        });
        toast.success('Campagne creee');
      }
      setEditingCampaign(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm('Supprimer cette campagne ?')) return;
    try {
      await adminAPI.deleteEmailCampaign(campaignId);
      toast.success('Campagne supprimee');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleToggleCampaign = async (campaign) => {
    try {
      await adminAPI.updateEmailCampaign(campaign.id, { is_active: !campaign.is_active });
      toast.success(campaign.is_active ? 'Campagne desactivee' : 'Campagne activee');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise a jour');
    }
  };

  const handleCancelScheduled = async (scheduledId) => {
    try {
      await adminAPI.cancelScheduledEmail(scheduledId);
      toast.success('Email annule');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'annulation');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      sent: { color: 'bg-blue-100 text-blue-800', icon: Send },
      delivered: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      opened: { color: 'bg-purple-100 text-purple-800', icon: Eye },
      clicked: { color: 'bg-indigo-100 text-indigo-800', icon: CheckCircle },
      failed: { color: 'bg-red-100 text-red-800', icon: XCircle },
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock }
    };
    const badge = badges[status] || badges.sent;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon size={12} />
        {status}
      </span>
    );
  };

  if (loading && activeTab === 'stats') {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="animate-spin" size={24} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex gap-2 border-b pb-2">
        {[
          { id: 'stats', label: 'Statistiques', icon: BarChart3 },
          { id: 'templates', label: 'Templates', icon: FileText },
          { id: 'campaigns', label: 'Campagnes', icon: Zap },
          { id: 'logs', label: 'Historique', icon: Mail }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              activeTab === tab.id
                ? 'bg-blue-900 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Stats Tab */}
      {activeTab === 'stats' && stats && (
        <div className="space-y-6">
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-sm text-blue-600">Emails envoyes</p>
              <p className="text-2xl font-bold text-blue-900">{stats.total_sent}</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <p className="text-sm text-green-600">Ouverts</p>
              <p className="text-2xl font-bold text-green-900">{stats.total_opened}</p>
              <p className="text-xs text-green-600">{stats.open_rate}% taux d'ouverture</p>
            </div>
            <div className="bg-purple-50 rounded-lg p-4">
              <p className="text-sm text-purple-600">Cliques</p>
              <p className="text-2xl font-bold text-purple-900">{stats.total_clicked}</p>
              <p className="text-xs text-purple-600">{stats.click_rate}% taux de clic</p>
            </div>
            <div className="bg-red-50 rounded-lg p-4">
              <p className="text-sm text-red-600">Echecs</p>
              <p className="text-2xl font-bold text-red-900">{stats.total_failed}</p>
            </div>
          </div>

          {/* Scheduled Emails */}
          {scheduledEmails.length > 0 && (
            <div>
              <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <Clock size={18} />
                Emails programmes ({stats.pending_scheduled})
              </h4>
              <div className="space-y-2">
                {scheduledEmails.map(scheduled => (
                  <div key={scheduled.id} className="flex items-center justify-between bg-yellow-50 rounded-lg p-3">
                    <div>
                      <p className="font-medium text-gray-800">{scheduled.recipient_email}</p>
                      <p className="text-sm text-gray-600">
                        <Calendar size={12} className="inline mr-1" />
                        {formatDate(scheduled.scheduled_for)}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCancelScheduled(scheduled.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <XCircle size={16} />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h4 className="font-semibold text-gray-800">Templates d'emails</h4>
            <Button
              size="sm"
              onClick={() => setEditingTemplate({ name: '', subject: '', html_content: '', is_active: true })}
            >
              <Plus size={16} className="mr-1" />
              Nouveau template
            </Button>
          </div>

          {/* Template Form */}
          {editingTemplate && (
            <div className="bg-gray-50 rounded-lg p-4 space-y-4">
              <Input
                placeholder="Nom du template (ex: welcome)"
                value={editingTemplate.name}
                onChange={(e) => setEditingTemplate({ ...editingTemplate, name: e.target.value })}
              />
              <Input
                placeholder="Sujet de l'email (variables: {{nom}}, {{email}})"
                value={editingTemplate.subject}
                onChange={(e) => setEditingTemplate({ ...editingTemplate, subject: e.target.value })}
              />
              <textarea
                className="w-full h-48 p-3 border rounded-lg font-mono text-sm"
                placeholder="Contenu HTML (variables: {{nom}}, {{score_url}}, {{dashboard_url}}, {{result_url}})"
                value={editingTemplate.html_content}
                onChange={(e) => setEditingTemplate({ ...editingTemplate, html_content: e.target.value })}
              />
              <div className="text-xs text-gray-500">
                Variables disponibles: {'{{nom}}'}, {'{{email}}'}, {'{{score_url}}'}, {'{{dashboard_url}}'}, {'{{result_url}}'}, {'{{dossier_id}}'}
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSaveTemplate}>
                  {editingTemplate.id ? 'Mettre a jour' : 'Creer'}
                </Button>
                <Button variant="outline" onClick={() => setEditingTemplate(null)}>
                  Annuler
                </Button>
              </div>
            </div>
          )}

          {/* Templates List */}
          <div className="space-y-2">
            {templates.map(template => (
              <div key={template.id} className="bg-white border rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h5 className="font-medium text-gray-800">{template.name}</h5>
                      {template.is_active ? (
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Actif</span>
                      ) : (
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full">Inactif</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{template.subject}</p>
                    {template.variables?.length > 0 && (
                      <p className="text-xs text-gray-400 mt-1">
                        Variables: {template.variables.join(', ')}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <div className="flex items-center gap-1">
                      <Input
                        type="email"
                        placeholder="Email test"
                        className="w-40 h-8 text-sm"
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                      />
                      <Button variant="ghost" size="sm" onClick={() => handleTestEmail(template.id)}>
                        <Send size={14} />
                      </Button>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setEditingTemplate(template)}>
                      <Edit2 size={14} />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteTemplate(template.id)} className="text-red-600">
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
            {templates.length === 0 && !loading && (
              <p className="text-center text-gray-500 py-4">Aucun template cree</p>
            )}
          </div>
        </div>
      )}

      {/* Campaigns Tab */}
      {activeTab === 'campaigns' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h4 className="font-semibold text-gray-800">Campagnes automatisees</h4>
            <Button
              size="sm"
              onClick={() => setEditingCampaign({ name: '', trigger_type: '', delay_minutes: 0, template_id: '', is_active: true })}
              disabled={templates.length === 0}
            >
              <Plus size={16} className="mr-1" />
              Nouvelle campagne
            </Button>
          </div>

          {templates.length === 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-800 text-sm flex items-center gap-2">
              <AlertCircle size={18} />
              Creez d'abord un template avant de pouvoir creer une campagne.
            </div>
          )}

          {/* Campaign Form */}
          {editingCampaign && (
            <div className="bg-gray-50 rounded-lg p-4 space-y-4">
              <Input
                placeholder="Nom de la campagne"
                value={editingCampaign.name}
                onChange={(e) => setEditingCampaign({ ...editingCampaign, name: e.target.value })}
              />
              <div className="grid grid-cols-2 gap-4">
                <Select
                  value={editingCampaign.trigger_type}
                  onValueChange={(value) => setEditingCampaign({ ...editingCampaign, trigger_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Declencheur" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(TRIGGER_TYPES).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={String(editingCampaign.delay_minutes)}
                  onValueChange={(value) => setEditingCampaign({ ...editingCampaign, delay_minutes: parseInt(value) })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Delai d'envoi" />
                  </SelectTrigger>
                  <SelectContent>
                    {DELAY_PRESETS.map(preset => (
                      <SelectItem key={preset.value} value={String(preset.value)}>{preset.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Select
                value={editingCampaign.template_id}
                onValueChange={(value) => setEditingCampaign({ ...editingCampaign, template_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Template a utiliser" />
                </SelectTrigger>
                <SelectContent>
                  {templates.filter(t => t.is_active).map(template => (
                    <SelectItem key={template.id} value={template.id}>{template.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex gap-2">
                <Button onClick={handleSaveCampaign}>
                  {editingCampaign.id ? 'Mettre a jour' : 'Creer'}
                </Button>
                <Button variant="outline" onClick={() => setEditingCampaign(null)}>
                  Annuler
                </Button>
              </div>
            </div>
          )}

          {/* Campaigns List */}
          <div className="space-y-2">
            {campaigns.map(campaign => {
              const template = templates.find(t => t.id === campaign.template_id);
              const delay = DELAY_PRESETS.find(d => d.value === campaign.delay_minutes);
              return (
                <div key={campaign.id} className="bg-white border rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h5 className="font-medium text-gray-800">{campaign.name}</h5>
                        {campaign.is_active ? (
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Active</span>
                        ) : (
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full">Inactive</span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <Zap size={14} />
                          {TRIGGER_TYPES[campaign.trigger_type]}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={14} />
                          {delay?.label || `${campaign.delay_minutes} min`}
                        </span>
                        <span className="flex items-center gap-1">
                          <FileText size={14} />
                          {template?.name || 'Template inconnu'}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleToggleCampaign(campaign)}
                      >
                        {campaign.is_active ? 'Desactiver' : 'Activer'}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setEditingCampaign(campaign)}>
                        <Edit2 size={14} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteCampaign(campaign.id)} className="text-red-600">
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
            {campaigns.length === 0 && !loading && (
              <p className="text-center text-gray-500 py-4">Aucune campagne creee</p>
            )}
          </div>
        </div>
      )}

      {/* Logs Tab */}
      {activeTab === 'logs' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h4 className="font-semibold text-gray-800">Historique des envois</h4>
            <Button variant="outline" size="sm" onClick={fetchData}>
              <RefreshCw size={14} className="mr-1" />
              Actualiser
            </Button>
          </div>

          <div className="space-y-2">
            {logs.map(log => (
              <div key={log.id} className="bg-white border rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-800">{log.recipient_email}</p>
                      {getStatusBadge(log.status)}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{log.subject}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      <Calendar size={12} className="inline mr-1" />
                      {formatDate(log.sent_at)}
                      {log.opened_at && (
                        <span className="ml-2">
                          <Eye size={12} className="inline mr-1" />
                          Ouvert: {formatDate(log.opened_at)}
                        </span>
                      )}
                    </p>
                    {log.error_message && (
                      <p className="text-xs text-red-600 mt-1">Erreur: {log.error_message}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {logs.length === 0 && !loading && (
              <p className="text-center text-gray-500 py-4">Aucun email envoye</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEmails;
